<div class="w-100 h-100">
    <div class="row" style="padding-top: 25px;">
        <div class="col-md-12">
            <div class="form-row justify-content-between align-items-baseline">
                <div class="col-auto">
                    <label class="header_text mb-3" style="color:#1a1a1a !important;">
                        {{$t('templateFilterCheck.title')}}
                    </label>
                    <!--
                        The verdict is only as current as the matching service's loaded catalogue, and
                        nothing else on the page says so - a just-approved template is simply absent, which
                        is reported as "not valid" and looks like a wrong answer.
                    -->
                    <p class="text-muted mb-0" style="font-size: 12px;">{{$t('templateFilterCheck.caveat')}}</p>
                </div>
            </div>
        </div>
    </div>

    <div class="row">
        <div class="col-sm-12">
            <div class="card card_layout">
                <div class="card-body card_body_layout">
                    <div class="row">
                        <div class="col-md-4">
                            <div class="form-group">
                                <label>{{$t('templateFilterCheck.sender')}}</label>
                                <!--
                                    Server-driven: no-filter because the API does the matching, and
                                    cache-items stays off so results from a previous query cannot leak
                                    into the current one. The chosen name is kept in pickerItems rather
                                    than cached, which is what makes hide-selected safe.
                                -->
                                <v-autocomplete ref="senderPicker"
                                                v-model="selectedSender"
                                                :search-input.sync="senderSearch"
                                                :items="pickerItems"
                                                item-text="senderName"
                                                item-value="senderName"
                                                :return-object="false"
                                                :cache-items="false"
                                                no-filter
                                                hide-selected
                                                clearable
                                                :loading="loadingSenders"
                                                :disabled="validating"
                                                :label="$t('templateFilterCheck.searchSenders')"
                                                :no-data-text="loadingSenders ? $t('templateFilterCheck.loadingSenders') : noSendersText"
                                                :menu-props="{ maxHeight: 300, offsetY: true, contentClass: 'template-filter-check-sender-menu' }"
                                                solo>
                                    <template v-slot:append-item>
                                        <div v-if="hasSenderSearchError"
                                             class="px-3 py-2 text-danger"
                                             style="font-size: 12px;">
                                            {{senderSearchErrorText}}
                                            <button class="btn btn-link p-0 ml-2"
                                                    @click="loadMoreSenders">{{$t('templateFilterCheck.retry')}}</button>
                                        </div>
                                        <div v-else-if="loadingSenders && senders.length > 0"
                                             class="px-3 py-2 text-muted"
                                             style="font-size: 12px;">
                                            {{$t('templateFilterCheck.loadingMoreSenders')}}
                                        </div>
                                    </template>
                                </v-autocomplete>
                            </div>
                        </div>
                    </div>

                    <div class="row">
                        <div class="col-md-12">
                            <div class="form-group">
                                <label>{{$t('templateFilterCheck.message')}}</label>
                                <!--
                                    Deliberately NOT capped with maxlength. The browser truncates an
                                    over-limit paste silently, and the page would then print a verdict for
                                    text the user never submitted - measurably the WRONG verdict when the
                                    discarded tail was what made it fail. The server rejects an over-limit
                                    message before any lookup or matching work, so letting it through is
                                    both cheap and honest. The counter below shows the limit instead.
                                -->
                                <textarea rows="5"
                                          class="form-control"
                                          style="resize: none"
                                          dir="auto"
                                          v-model="message"
                                          :disabled="validating || maxMessageLength === null"
                                          :placeholder="$t('templateFilterCheck.messagePlaceholder')"></textarea>
                                <small class="form-text text-muted d-block text-right"
                                       v-if="maxMessageLength"
                                       :class="{ 'text-danger': message.length > maxMessageLength }">
                                    {{ message.length }} / {{ maxMessageLength }}
                                </small>
                            </div>
                        </div>
                    </div>

                    <div class="row">
                        <div class="col-md-12">
                            <button class="btn btn-primary px-5 py-2"
                                    @click="validate"
                                    :disabled="!canValidate">
                                {{$t('templateFilterCheck.validate')}}
                            </button>
                        </div>
                    </div>

                    <div class="row" v-if="hasLoadError">
                        <div class="col-md-12 mt-4">
                            <p class="text-danger mb-0">{{loadErrorText}}</p>
                            <!--
                                The message says "Try again", so there has to be something to try with:
                                the limit and the sender list both come from this one call, and without it
                                the page cannot submit at all. loadSenders is idempotent and clears the
                                error on entry.
                            -->
                            <button class="btn btn-link p-0 mt-2"
                                    @click="loadSenders"
                                    :disabled="loadingSenders">{{$t('templateFilterCheck.retry')}}</button>
                        </div>
                    </div>

                    <div class="row" v-if="hasError">
                        <div class="col-md-12 mt-4">
                            <p class="text-danger mb-0">{{errorText}}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div class="row" v-if="hasResult">
        <div class="col-sm-12">
            <div class="card card_layout">
                <div class="card-body card_body_layout">
                    <div class="row">
                        <div class="col-md-12">
                            <label class="user_info_header mb-3" v-if="result.valid">
                                <span class="text-success">{{$t('templateFilterCheck.resultValid')}}</span>
                            </label>
                            <label class="user_info_header mb-3" v-else>
                                <span class="text-danger">{{$t('templateFilterCheck.resultNotValid')}}</span>
                            </label>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-md-3">
                            <div class="form-group">
                                <label>{{$t('templateFilterCheck.similarity')}}</label>
                                <p class="mb-0">{{similarityText}}</p>
                            </div>
                        </div>
                        <div class="col-md-3">
                            <div class="form-group">
                                <label>{{$t('templateFilterCheck.time')}}</label>
                                <p class="mb-0">{{timeText}}</p>
                            </div>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-md-12">
                            <div class="form-group">
                                <label>{{$t('templateFilterCheck.matchedTemplate')}}</label>
                                <textarea rows="3" class="form-control" style="resize: none"
                                          :value="matchedTemplateText" readonly></textarea>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>
